import type { BadgeShapeId } from './types'

type BadgeShapeGeometry =
  | {
      cx: number
      cy: number
      kind: 'circle'
      r: number
    }
  | {
      d: string
      kind: 'path'
    }
  | {
      height: number
      kind: 'rect'
      rx?: number
      width: number
      x: number
      y: number
    }

interface BadgeShapeSpec {
  fill: BadgeShapeGeometry
  outline: BadgeShapeGeometry
  sourceHeight: number
  sourceWidth: number
}

export const BADGE_VIEW_BOX = '0 0 100 112'

// These shapes come from the Figma "Shapes" frame at node 836:4278.
export const BADGE_SHAPE_SPECS: Record<BadgeShapeId, BadgeShapeSpec> = {
  circle: {
    fill: {
      cx: 140,
      cy: 132,
      kind: 'circle',
      r: 116
    },
    outline: {
      cx: 140,
      cy: 132,
      kind: 'circle',
      r: 111
    },
    sourceHeight: 280,
    sourceWidth: 280
  },
  diamond: {
    fill: {
      d: 'M125.574 21.858C133.384 14.0475 146.047 14.0475 153.858 21.858L249.574 117.574C257.384 125.384 257.384 138.047 249.574 145.858L153.858 241.574C146.047 249.384 133.384 249.384 125.574 241.574L29.858 145.858C22.0475 138.047 22.0475 125.384 29.858 117.574L125.574 21.858Z',
      kind: 'path'
    },
    outline: {
      d: 'M129.109 25.3936C134.967 19.5358 144.464 19.5358 150.322 25.3936L246.038 121.109C251.896 126.967 251.896 136.464 246.038 142.322L150.322 238.038C144.464 243.896 134.967 243.896 129.109 238.038L33.3936 142.322C27.5358 136.464 27.5358 126.967 33.3936 121.109L129.109 25.3936Z',
      kind: 'path'
    },
    sourceHeight: 280,
    sourceWidth: 280
  },
  hexagon: {
    fill: {
      d: 'M121.387 18.6798C127.575 15.1071 135.199 15.1071 141.387 18.6798L228.774 69.1327C234.962 72.7054 238.774 79.3079 238.774 86.4533V187.359C238.774 194.505 234.962 201.107 228.774 204.68L141.387 255.133C135.199 258.705 127.575 258.705 121.387 255.133L34.0001 204.68C27.812 201.107 24.0001 194.505 24.0001 187.359V86.4533C24.0001 79.3079 27.812 72.7054 34.0001 69.1327L121.387 18.6798Z',
      kind: 'path'
    },
    outline: {
      d: 'M123.887 23.0098C128.528 20.3303 134.246 20.3303 138.887 23.0098L226.274 73.4629C230.915 76.1424 233.774 81.0942 233.774 86.4531V187.359C233.774 192.718 230.915 197.67 226.274 200.35L138.887 250.803C134.391 253.398 128.885 253.479 124.326 251.046L123.887 250.803L36.5005 200.35C31.8595 197.67 29.0005 192.718 29.0005 187.359V86.4531C29.0005 81.0942 31.8595 76.1424 36.5005 73.4629L123.887 23.0098Z',
      kind: 'path'
    },
    sourceHeight: 290,
    sourceWidth: 263
  },
  roundedSquare: {
    fill: {
      height: 212,
      kind: 'rect',
      rx: 20,
      width: 212,
      x: 24,
      y: 16
    },
    outline: {
      height: 202,
      kind: 'rect',
      rx: 15,
      width: 202,
      x: 29,
      y: 21
    },
    sourceHeight: 260,
    sourceWidth: 260
  },
  rosette: {
    fill: {
      d: 'M122.993 21.9533C130.676 14.0162 143.404 14.0162 151.086 21.9533C156.469 27.5145 164.626 29.3764 171.889 26.7014C182.255 22.8837 193.722 28.4062 197.2 38.8906C199.637 46.2367 206.179 51.4534 213.883 52.1946C224.878 53.2524 232.814 63.2036 231.399 74.1588C230.407 81.8347 234.037 89.3731 240.657 93.3836C250.105 99.1074 252.937 111.516 246.908 120.772C242.684 127.258 242.684 135.625 246.908 142.11C252.937 151.367 250.105 163.775 240.657 169.499C234.037 173.51 230.407 181.048 231.399 188.724C232.814 199.679 224.878 209.63 213.883 210.688C206.179 211.429 199.637 216.646 197.2 223.992C193.722 234.477 182.255 239.999 171.889 236.181C164.626 233.506 156.469 235.368 151.086 240.93C143.404 248.867 130.676 248.867 122.993 240.93C117.61 235.368 109.453 233.506 102.19 236.181C91.8244 239.999 80.3569 234.477 76.8789 223.992C74.4419 216.646 67.9004 211.429 60.1962 210.688C49.2007 209.63 41.2649 199.679 42.6804 188.724C43.6721 181.048 40.0418 173.51 33.4222 169.499C23.9746 163.775 21.1423 151.367 27.1708 142.11C31.3949 135.625 31.3949 127.258 27.1708 120.772C21.1423 111.516 23.9746 99.1074 33.4222 93.3836C40.0418 89.3731 43.6721 81.8347 42.6804 74.1587C41.2649 63.2036 49.2007 53.2524 60.1962 52.1946C67.9004 51.4534 74.4419 46.2367 76.8789 38.8906C80.3569 28.4062 91.8244 22.8837 102.19 26.7014C109.453 29.3764 117.61 27.5145 122.993 21.9533Z',
      kind: 'path'
    },
    outline: {
      d: 'M126.585 25.4307C132.303 19.5236 141.776 19.5236 147.494 25.4307C154.253 32.4142 164.496 34.7524 173.617 31.3936C181.331 28.5523 189.866 32.662 192.455 40.4648C195.515 49.6898 203.729 56.241 213.404 57.1719C221.587 57.9591 227.493 65.3654 226.44 73.5186C225.195 83.1576 229.753 92.624 238.066 97.6602C245.097 101.92 247.205 111.155 242.718 118.044C237.414 126.188 237.414 136.695 242.718 144.839C247.205 151.728 245.097 160.963 238.066 165.223C229.753 170.259 225.195 179.725 226.44 189.364C227.493 197.517 221.587 204.924 213.404 205.711C203.729 206.642 195.515 213.193 192.455 222.418C189.866 230.221 181.331 234.331 173.617 231.489C164.496 228.13 154.253 230.469 147.494 237.452C141.776 243.359 132.303 243.359 126.585 237.452C119.826 230.469 109.583 228.13 100.462 231.489C92.748 234.331 84.213 230.221 81.6245 222.418C78.5643 213.193 70.3499 206.642 60.6753 205.711C52.4923 204.924 46.586 197.518 47.6392 189.365C48.8846 179.726 44.3259 170.259 36.0132 165.223C28.982 160.963 26.8742 151.728 31.3608 144.839C36.6649 136.695 36.6649 126.188 31.3608 118.044C26.8742 111.155 28.982 101.92 36.0132 97.6602C44.3259 92.6239 48.8846 83.1568 47.6392 73.5176C46.586 65.3646 52.4923 57.9591 60.6753 57.1719C70.3499 56.241 78.5643 49.6898 81.6245 40.4648C84.213 32.662 92.748 28.5523 100.462 31.3936C109.583 34.7524 119.826 32.4142 126.585 25.4307Z',
      kind: 'path'
    },
    sourceHeight: 279,
    sourceWidth: 275
  },
  seal: {
    fill: {
      d: 'M139.247 17.6773C142.132 15.441 146.164 15.441 149.049 17.6773L177.321 39.5928C178.64 40.6151 180.245 41.1995 181.913 41.2641L217.657 42.6484C221.304 42.7897 224.394 45.3819 225.166 48.9494L232.736 83.9103C233.089 85.5411 233.944 87.0207 235.179 88.142L261.671 112.178C264.375 114.631 265.075 118.603 263.374 121.832L246.7 153.48C245.923 154.956 245.626 156.639 245.852 158.292L250.695 193.734C251.19 197.35 249.173 200.843 245.794 202.223L212.679 215.749C211.134 216.38 209.825 217.478 208.936 218.89L189.865 249.153C187.919 252.241 184.129 253.621 180.653 252.506L146.591 241.581C145.002 241.072 143.294 241.072 141.705 241.581L107.643 252.506C104.167 253.621 100.377 252.241 98.4312 249.153L79.3602 218.89C78.4705 217.478 77.1617 216.38 75.617 215.749L42.5018 202.223C39.1226 200.843 37.1063 197.35 37.6005 193.734L42.4441 158.292C42.6701 156.639 42.3734 154.956 41.5956 153.48L24.9223 121.832C23.221 118.603 23.9212 114.631 26.6245 112.178L53.1164 88.142C54.3522 87.0207 55.2065 85.5411 55.5596 83.9103L63.1299 48.9494C63.9024 45.3819 66.9916 42.7897 70.6391 42.6484L106.383 41.2641C108.051 41.1995 109.656 40.6151 110.975 39.5928L139.247 17.6773Z',
      kind: 'path'
    },
    outline: {
      d: 'M142.31 21.6289C143.392 20.7904 144.904 20.7904 145.986 21.6289L174.257 43.5449C176.4 45.2062 179.01 46.1558 181.719 46.2607L217.463 47.6445C218.831 47.6975 219.989 48.67 220.279 50.0078L227.849 84.9688C228.423 87.6188 229.812 90.0227 231.82 91.8447L258.312 115.882C259.325 116.802 259.588 118.291 258.95 119.502L242.277 151.149C241.013 153.548 240.531 156.282 240.898 158.969L245.742 194.41C245.927 195.766 245.171 197.076 243.904 197.594L210.789 211.12C208.278 212.145 206.151 213.931 204.706 216.225L185.634 246.487C184.905 247.645 183.484 248.163 182.18 247.745L148.118 236.82C145.536 235.992 142.76 235.992 140.178 236.82L106.116 247.745C104.812 248.163 103.391 247.645 102.662 246.487L83.5903 216.225C82.1447 213.931 80.0175 212.145 77.5073 211.12L44.3921 197.594C43.1252 197.076 42.3689 195.766 42.5542 194.41L47.3979 158.969C47.7651 156.282 47.2829 153.548 46.019 151.149L29.3462 119.502C28.7083 118.291 28.9705 116.802 29.9839 115.882L56.4761 91.8447C58.4841 90.0227 59.8729 87.6188 60.4468 84.9688L68.0171 50.0078C68.3068 48.67 69.4648 47.6975 70.8325 47.6445L106.577 46.2607C109.286 46.1558 111.896 45.2062 114.039 43.5449L142.31 21.6289Z',
      kind: 'path'
    },
    sourceHeight: 285,
    sourceWidth: 289
  },
  shield: {
    fill: {
      d: 'M125.929 240C137.238 240 250.846 208.267 219.574 44.8082C218.841 40.9793 216.802 37.491 213.435 35.5253C202.541 29.1638 173.802 16.1695 125.927 16.0016C77.736 15.8325 46.7187 29.1963 35.0725 35.6072C31.5969 37.5205 29.5035 41.0244 28.7995 44.9289C-0.654259 208.283 114.623 240 125.929 240Z',
      kind: 'path'
    },
    outline: {
      d: 'M125.909 21.002C172.857 21.1667 200.738 33.9011 210.914 39.8428C212.757 40.9186 214.135 42.9889 214.663 45.748C230.05 126.176 209.711 173.302 185.027 200.329C172.591 213.946 158.873 222.661 147.601 227.964C141.966 230.615 136.973 232.398 133.101 233.512C129.074 234.669 126.615 235 125.929 235C125.236 235 122.747 234.668 118.667 233.509C114.741 232.394 109.672 230.607 103.941 227.954C92.4764 222.647 78.4876 213.927 65.7285 200.304C40.4033 173.264 19.2335 126.159 33.7197 45.8164C34.218 43.0528 35.6052 41.0213 37.4834 39.9873C48.4747 33.9368 78.6634 20.8363 125.909 21.002Z',
      kind: 'path'
    },
    sourceHeight: 272,
    sourceWidth: 249
  }
}

export function getBadgeShapeSpec(shapeId: BadgeShapeId) {
  return BADGE_SHAPE_SPECS[shapeId]
}

export function getBadgeShapeTransform(shapeId: BadgeShapeId) {
  const { sourceHeight, sourceWidth } = BADGE_SHAPE_SPECS[shapeId]
  const scale = Number(
    Math.min(100 / sourceWidth, 112 / sourceHeight).toFixed(6)
  )
  const translateX = Number(((100 - sourceWidth * scale) / 2).toFixed(6))
  const translateY = Number(((112 - sourceHeight * scale) / 2).toFixed(6))

  return `matrix(${scale} 0 0 ${scale} ${translateX} ${translateY})`
}
